import { useState, useEffect } from "react";
import { Star, Send, CheckCircle, XCircle, Clock, Trash2, Copy, ExternalLink, User } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestimonialManager() {
  const [testimonials, setTestimonials] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState("testimonials");
  const [showNew, setShowNew] = useState(false);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ client_name: "", client_email: "", project_id: "" });
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [t, r, p] = await Promise.all([
        axios.get(`${API}/admin/testimonials`),
        axios.get(`${API}/admin/testimonials/requests`),
        axios.get(`${API}/admin/projects`).catch(() => ({ data: [] })),
      ]);
      setTestimonials(t.data);
      setRequests(r.data);
      setProjects(Array.isArray(p.data) ? p.data : []);
    } catch (e) { console.error("Testimonials load error:", e); }
  };

  useEffect(() => { load(); }, []);

  const sendRequest = async () => {
    if (!form.client_name || !form.client_email) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/testimonials/request`, form);
      toast.success(`Testimonial-Anfrage an ${form.client_email} gesendet`);
      setForm({ client_name: "", client_email: "", project_id: "" });
      setShowNew(false);
      load();
    } catch (e) { console.error(e); toast.error("Fehler beim Senden"); }
    setSending(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/testimonials/${id}/status`, { status });
      toast.success(status === "approved" ? "Freigegeben" : "Abgelehnt");
      load();
    } catch (e) { console.error(e); toast.error("Fehler"); }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm("Testimonial löschen?")) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`);
      toast.success("Gelöscht");
      load();
    } catch (e) { console.error("Delete testimonial error:", e); }
  };

  const selectProject = (pid) => {
    const p = projects.find((x) => x.id === pid);
    if (p) setForm({ ...form, client_name: p.client_name, client_email: p.client_email || "", project_id: pid });
  };

  const statusBadge = (s) => {
    const map = {
      pending: { icon: Clock, label: "Ausstehend", cls: "bg-amber-50 text-amber-700 border-amber-200" },
      approved: { icon: CheckCircle, label: "Freigegeben", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
      rejected: { icon: XCircle, label: "Abgelehnt", cls: "bg-red-50 text-red-700 border-red-200" },
      submitted: { icon: CheckCircle, label: "Eingereicht", cls: "bg-blue-50 text-blue-700 border-blue-200" },
    };
    const m = map[s] || map.pending;
    const Icon = m.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border ${m.cls}`}>
        <Icon className="w-3 h-3" />{m.label}
      </span>
    );
  };

  const frontendUrl = process.env.REACT_APP_BACKEND_URL?.replace("/api", "") || "";

  return (
    <div data-testid="testimonial-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-[#0F172A]">Testimonials</h2>
          <p className="text-sm text-[#64748B] mt-1">
            {testimonials.filter((t) => t.status === "approved").length} freigegeben &middot; {testimonials.filter((t) => t.status === "pending").length} ausstehend
          </p>
        </div>
        <button onClick={() => setShowNew(!showNew)}
          className="flex items-center gap-2 bg-[#0F172A] text-[#D4A373] px-4 py-2.5 text-sm font-medium hover:bg-[#1E293B] transition-colors"
          data-testid="testimonial-new-request-btn">
          <Send className="w-4 h-4" />Neue Anfrage
        </button>
      </div>

      {/* Auto-Request Info */}
      <div className="bg-[#0F172A]/5 border border-[#0F172A]/10 px-4 py-3 mb-6 flex items-center gap-3" data-testid="testimonial-auto-info">
        <Clock className="w-4 h-4 text-[#D4A373] flex-shrink-0" />
        <p className="text-xs text-[#64748B]">
          <span className="font-medium text-[#0F172A]">Automatisch:</span> Testimonial-Anfragen werden 14 Tage nach Projektabschluss automatisch versendet.
        </p>
      </div>

      {/* New Request Form */}
      {showNew && (
        <div className="bg-white border border-[#e2e5e9] p-5 mb-6" data-testid="testimonial-request-form">
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#94a3b8] mb-4">Testimonial anfragen</p>
          {projects.length > 0 && (
            <div className="mb-4">
              <label className="block text-xs text-[#64748B] mb-1.5">Aus Kundenstamm wählen</label>
              <select onChange={(e) => selectProject(e.target.value)} value={form.project_id}
                className="w-full px-3 py-2 border border-[#e2e5e9] text-sm bg-white" data-testid="testimonial-select-project">
                <option value="">— Manuell eingeben —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.client_name}{p.client_email ? ` (${p.client_email})` : ""}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-xs text-[#64748B] mb-1.5">Name *</label>
              <input type="text" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                className="w-full px-3 py-2 border border-[#e2e5e9] text-sm" data-testid="testimonial-req-name" />
            </div>
            <div>
              <label className="block text-xs text-[#64748B] mb-1.5">E-Mail *</label>
              <input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                className="w-full px-3 py-2 border border-[#e2e5e9] text-sm" data-testid="testimonial-req-email" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={sendRequest} disabled={sending || !form.client_name || !form.client_email}
              className="bg-[#D4A373] text-[#0F172A] px-5 py-2 text-sm font-medium disabled:opacity-50 hover:bg-[#C4956A] transition-colors"
              data-testid="testimonial-send-btn">
              {sending ? "Wird gesendet..." : "Anfrage senden"}
            </button>
            <button onClick={() => setShowNew(false)} className="px-4 py-2 text-sm text-[#64748B] hover:text-[#0F172A]">Abbrechen</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-[#e2e5e9]">
        {[{ id: "testimonials", label: `Testimonials (${testimonials.length})` }, { id: "requests", label: `Anfragen (${requests.length})` }].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#D4A373] text-[#0F172A]" : "border-transparent text-[#64748B] hover:text-[#0F172A]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Testimonials List */}
      {tab === "testimonials" && (
        <div className="space-y-3">
          {testimonials.length === 0 ? (
            <p className="text-sm text-[#94a3b8] py-8 text-center">Noch keine Testimonials eingegangen.</p>
          ) : testimonials.map((t) => (
            <div key={t.id} className="bg-white border border-[#e2e5e9] p-5" data-testid={`testimonial-item-${t.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-3">
                  {t.photo_url ? (
                    <img src={t.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#F8F9FA] flex items-center justify-center"><User className="w-5 h-5 text-[#94a3b8]" /></div>
                  )}
                  <div>
                    <p className="font-medium text-sm text-[#0F172A]">{t.client_name}</p>
                    <p className="text-xs text-[#64748B]">{[t.position, t.company].filter(Boolean).join(" — ") || "Keine Angabe"}</p>
                  </div>
                </div>
                {statusBadge(t.status)}
              </div>
              <div className="flex gap-0.5 mb-3">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className={`w-4 h-4 ${n <= t.rating ? "fill-[#D4A373] text-[#D4A373]" : "text-[#e2e5e9]"}`} />
                ))}
              </div>
              <p className="text-sm text-[#1E293B] leading-relaxed mb-4 italic">"{t.text}"</p>
              <div className="flex items-center gap-2">
                {t.status !== "approved" && (
                  <button onClick={() => updateStatus(t.id, "approved")}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200 hover:bg-emerald-100"
                    data-testid={`approve-${t.id}`}>
                    <CheckCircle className="w-3.5 h-3.5" />Freigeben
                  </button>
                )}
                {t.status !== "rejected" && (
                  <button onClick={() => updateStatus(t.id, "rejected")}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 text-xs font-medium border border-red-200 hover:bg-red-100"
                    data-testid={`reject-${t.id}`}>
                    <XCircle className="w-3.5 h-3.5" />Ablehnen
                  </button>
                )}
                <button onClick={() => deleteTestimonial(t.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[#64748B] text-xs hover:text-red-600 ml-auto"
                  data-testid={`delete-${t.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Requests List */}
      {tab === "requests" && (
        <div className="space-y-2">
          {requests.length === 0 ? (
            <p className="text-sm text-[#94a3b8] py-8 text-center">Noch keine Anfragen gesendet.</p>
          ) : requests.map((r) => (
            <div key={r.id} className="bg-white border border-[#e2e5e9] p-4 flex items-center justify-between" data-testid={`request-item-${r.id}`}>
              <div>
                <p className="text-sm font-medium text-[#0F172A]">
                  {r.client_name}
                  {r.auto_generated && <span className="ml-2 text-[9px] font-semibold tracking-widest uppercase text-[#D4A373] border border-[#D4A373]/30 px-1.5 py-0.5">Auto</span>}
                </p>
                <p className="text-xs text-[#64748B]">{r.client_email} &middot; {new Date(r.created_at).toLocaleDateString("de-CH")}</p>
              </div>
              <div className="flex items-center gap-3">
                {statusBadge(r.status)}
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${frontendUrl}/testimonial/${r.token}`);
                    toast.success("Link kopiert");
                  }}
                  className="p-1.5 text-[#64748B] hover:text-[#0F172A]" title="Link kopieren"
                  data-testid={`copy-link-${r.id}`}>
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
